import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { GenericAuthStatusTableData } from "../../../../data-model/tablesData";
import { TableNames } from "../../../../enums/tableNames";
import { RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { makePostCallForInitialDataFetch } from "../apis/makePostCallForInitialDataFetch";
import { generateFilterPayloadForGenericAuthStatus } from "../utils/generateFilterPayloadForGenericAuthStatus";
import { recursivelyGeneratePayloadForInitialFetch } from "../utils/recursivelyGeneratePayloadForInitialFetch";
import { GenericAuthStatusState } from "./useGenericAuthStatusTableData";
import { useValidFilters } from "./useValidFilters";

/**
 * Initial data fetching hook with pagination restoration
 *
 * Handles the first data fetch when component mounts, including restoration
 * of previous pagination state from table context.
 *
 * Features:
 * - Waits for authStatusOptions, facilityId, and tabId to be available
 * - Retrieves last viewed page from table context
 * - Generates payloads for all pages up to the last viewed page
 * - Fetches all pages recursively to restore previous state
 * - Sets initialDataFetchingExecuted flag when complete
 *
 * The setter is exposed to allow manual refresh by setting the flag back to false.
 *
 * @param {Function} setGenericAuthStatusState - API state setter
 * @param {Function} setTableData - Table data setter
 * @param {string} tabId - Auth status tab identifier (e.g., "pending", "hold")
 * @returns {Object} Initial fetch state
 * @returns {boolean} returns.initialDataFetchingExecuted - Flag indicating fetch completion
 * @returns {Function} returns.setInitialDataFetchingExecuted - Setter to trigger refetch
 *
 * @example
 * const { initialDataFetchingExecuted, setInitialDataFetchingExecuted } =
 *   useInitialDataFetchingForTable(setGenericAuthStatusState, setTableData, "pending");
 *
 * // Trigger manual refresh
 * setInitialDataFetchingExecuted(false);
 */
export const useInitialDataFetchingForTable = (
  setGenericAuthStatusState: React.Dispatch<
    React.SetStateAction<GenericAuthStatusState>
  >,
  setTableData: React.Dispatch<
    React.SetStateAction<GenericAuthStatusTableData[]>
  >,
  tabId: string,
) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );
  const [initialDataFetchingExecuted, setInitialDataFetchingExecuted] =
    useState(false);

  const orgId = getOrgIdForFetchExternalWorklist();

  const { getTableDataFromContext } = useTablesContext();

  const tableAttributes = getTableDataFromContext(
    `${TableNames.GENERIC_AUTH_STATUS_ORDERS}_${tabId}`,
  );

  const validFilters = useValidFilters(tabId);

  useEffect(() => {
    if (
      !initialDataFetchingExecuted &&
      authStatusOptions &&
      authStatusOptions.length > 0 &&
      providerDetails?.FacilityId &&
      tabId
    ) {
      const payload = generateFilterPayloadForGenericAuthStatus(
        validFilters,
        tableAttributes?.searchedText ?? "",
        orgId,
        user?.id ?? "",
        providerDetails.FacilityId,
        providerDetails.IsAdmin ?? false,
        tabId,
        tableAttributes?.sortByField,
        tableAttributes?.sortAscending,
      );
      const payloads = recursivelyGeneratePayloadForInitialFetch(
        payload,
        tableAttributes?.lastBatchFetched ?? 0,
        tableAttributes?.sortByField ?? "",
        tableAttributes?.sortAscending ?? true,
      );

      makePostCallForInitialDataFetch(
        payloads,
        authStatusOptions,
        setGenericAuthStatusState,
        setTableData,
        setInitialDataFetchingExecuted,
      );
    }
  }, [
    authStatusOptions,
    initialDataFetchingExecuted,
    providerDetails?.FacilityId,
    tabId,
  ]);

  return {
    initialDataFetchingExecuted,
    setInitialDataFetchingExecuted,
  };
};
